import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApPurchaseDocument } from "@/accounts-payable/types";
import { useApInvoiceDocument } from "@/accounts-payable/use-invoice-document";
import { documentLabel } from "@/accounts-payable/utils";
import { SectionHeading } from "./shared";

/** The document preview in a purchase document's detail panel. */
export const DocumentPreviewSection = ({
  document: purchaseDocument,
}: {
  document: ApPurchaseDocument;
}) => {
  const kind = purchaseDocument.document_type ?? "invoice";
  const label = documentLabel(purchaseDocument);
  const {
    objectUrl: documentUrl,
    isLoading: documentLoading,
    isError: documentError,
  } = useApInvoiceDocument(purchaseDocument.file_url);
  return (
    <Stack gap="md" fullWidth>
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap="md"
        fullWidth
        className="flex-wrap"
      >
        <SectionHeading>Document</SectionHeading>
        {documentUrl && (
          <a
            href={documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-md text-interactive-text hover:underline"
          >
            Open in new tab
          </a>
        )}
      </Stack>
      {purchaseDocument.file_url ? (
        documentError ? (
          <Typography textColor="tertiary">
            Couldn’t load the {kind} document.
          </Typography>
        ) : !documentUrl || documentLoading ? (
          <Typography textColor="tertiary">Loading document…</Typography>
        ) : purchaseDocument.file_content_type?.startsWith("image/") ? (
          <img
            src={documentUrl}
            alt={label}
            className="w-full rounded-lg border border-stroke-tertiary"
          />
        ) : (
          <iframe
            src={documentUrl}
            title={label}
            className="h-[460px] w-full rounded-lg border border-stroke-tertiary"
          />
        )
      ) : (
        <Typography textColor="tertiary">
          No document available for this {kind}.
        </Typography>
      )}
    </Stack>
  );
};
